import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';

export interface LedgerHoverCardProps {
  title: ReactNode;
  /** Quiet word after the title: "Labour item", "Deduction", a work type. */
  kind?: ReactNode;
  /** Free text under the heading — where a demoted description reappears. */
  description?: ReactNode;
  /** The facts, usually a list of `<Fact>` nodes. */
  children?: ReactNode;
  onOpenDetails?: () => void;
  openDetailsLabel?: string;
}

/**
 * The row hover card: a shortcut to the side panel. Truncated and demoted
 * facts reappear here, so which facts exist is entirely the caller's call.
 */
export const LedgerHoverCard = ({
  title,
  kind,
  description,
  children,
  onOpenDetails,
  openDetailsLabel = 'Open details',
}: LedgerHoverCardProps) => (
  <Box sx={{ p: 1.5, minWidth: 230, maxWidth: 300 }}>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.25 }}>
      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 0 }}>
        {title}
      </Typography>
      {kind && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ flexShrink: 0 }}
        >
          {kind}
        </Typography>
      )}
    </Box>
    {description && (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 0.75 }}
      >
        {description}
      </Typography>
    )}
    {children}
    {onOpenDetails && (
      <Typography
        component="button"
        variant="caption"
        onClick={onOpenDetails}
        sx={{
          display: 'block',
          mt: 0.75,
          p: 0,
          border: 0,
          bgcolor: 'transparent',
          color: 'primary.main',
          cursor: 'pointer',
          font: 'inherit',
          fontSize: 'inherit',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        {openDetailsLabel}
      </Typography>
    )}
  </Box>
);
